import { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Chip,
    Skeleton,
    useTheme,
    Menu,
    MenuItem,
    ListItemIcon,
    Button,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    DialogContentText,
    Tooltip,
    TextField,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import {
    Close,
    Groups,
    Person,
    MoreVert,
    AdminPanelSettings,
    PersonRemove,
    ExitToApp,
    Security,
    VerifiedUser,
    PersonAdd,
    Edit,
    ArrowBack,
    Search,
    CheckCircle,
    AddCircleOutline
} from '@mui/icons-material';
import InfiniteScroll from 'react-infinite-scroll-component'; // IMPORT ADDED

import {
    chatSettings,
    removeMember,
    makeAdmin,
    revokeAdmin,
    exitGroup,
    addMember
} from '../../services/Chat';
import EditGroupDetails from './EditGroupDetails';
import { getConnections } from '../../services/UserProfileApi';
import MentorBadge from '../../components/MentorBadge';

const ChatDetail = ({ setRecentChats, showSnackbar, setSelectedChat, chat, onClose, currentUserId, getImageUrlWithToken, username, navigate, closeChat }) => {
    const theme = useTheme();

    // --- State: Data ---
    const [chatDetails, setChatDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    // --- State: Connections (Infinite Scroll) ---
    const [connections, setConnections] = useState([]);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // --- State: UI Control ---
    const [view, setView] = useState('main'); // 'main' | 'add_member'

    // --- State: Search ---
    const [memberSearchQuery, setMemberSearchQuery] = useState("");
    const [connectionSearchQuery, setConnectionSearchQuery] = useState("");

    // --- State: Dialogs ---
    const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    // --- State: Menus ---
    const [headerAnchorEl, setHeaderAnchorEl] = useState(null);
    const isHeaderMenuOpen = Boolean(headerAnchorEl);

    // 2. Use it in useEffect on mount
    useEffect(() => {
        if (!chat?.chat_id) return;
        setLoading(true);
        chatSettings(chat.chat_id)
            .then((res) => setChatDetails(res))
            .finally(() => setLoading(false));
    }, [chat?.chat_id]);

    // 2. Fetch Connections
    // This effect runs whenever 'page' changes or when view switches to 'add_member'
    useEffect(() => {
        if (view === 'add_member') {
            // Only show loading spinner on initial load (page 1) or if list is empty
            if (page === 1) setLoadingConnections(true);

            getConnections(username, page, connectionSearchQuery)
                .then((res) => {
                    const newConnections = res.connections || [];

                    setConnections(prev => {
                        return page === 1 ? newConnections : [...prev, ...newConnections];
                    });

                    // Logic to stop infinite scroll if no more data
                    // Adjust '10' to your API's page size
                    if (newConnections.length === 0 || newConnections.length < 10) {
                        setHasMore(false);
                    } else {
                        setHasMore(true);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching connections:", err);
                    setHasMore(false); // Stop trying on error
                })
                .finally(() => setLoadingConnections(false));
        }
    }, [view, username, page, connectionSearchQuery]);


    // --- Derived Logic ---
    const isGroup = chatDetails?.is_group === 1;
    const memberList = chatDetails?.members?.members || [];

    // Filter Existing Members based on Search
    const filteredMembers = useMemo(() => {
        if (!memberSearchQuery) return memberList;
        return memberList.filter(m =>
            m.name?.toLowerCase().includes(memberSearchQuery.toLowerCase())
        );
    }, [memberList, memberSearchQuery]);

    // Helper: Check if a connection is already in the group
    const isAlreadyMember = (connectionId) => {
        return memberList.some(m => String(m.user_id) === String(connectionId));
    };

    // ADMIN CHECK
    const amIAdmin = useMemo(() => {
        const me = memberList.find(m => String(m.user_id) === String(currentUserId));
        return Boolean(me?.admin) || me?.admin === 1;
    }, [memberList, currentUserId]);

    const displayInfo = useMemo(() => {
        if (!chatDetails) return {};
        if (isGroup) {
            const fallbackName = memberList.slice(0, 3).map(m => m.name?.split(' ')[0]).join(', ') + '...';
            return {
                name: chatDetails.chat_name || fallbackName || "Unnamed Group",
                image: chatDetails.chat_image,
                desc: chatDetails.description || `${memberList.length} participants`,
                totalMembers: chatDetails.members?.total_members || memberList.length
            };
        } else {
            const other = memberList.find(m => String(m.user_id) !== String(currentUserId));
            return {
                name: other?.name || "Unknown",
                image: other?.profile_pic,
                desc: other?.describes_me,
                totalMembers: 0,
                mentor: other?.is_mentor,
                username: other?.username,
                is_verified: other?.is_verified
            };
        }
    }, [chatDetails, isGroup, currentUserId, memberList]);

    // --- Handlers ---
    const handleHeaderMenuOpen = (event) => setHeaderAnchorEl(event.currentTarget);
    const handleHeaderMenuClose = () => setHeaderAnchorEl(null);

    const handleClick = (action, userId) => {
        let promise;
        if (action === 'make_admin') promise = makeAdmin(chat.chat_id, userId);
        else if (action === 'revoke_admin') promise = revokeAdmin(chat.chat_id, userId);
        else if (action === 'remove_member') promise = removeMember(chat.chat_id, userId);

        if (promise) {
            promise
                .then(() => chatSettings(chat.chat_id))
                .then((updatedDetails) => setChatDetails(updatedDetails))
                .catch((err) => {
                    console.error(`Error performing action ${action} on user ${userId}:`, err);
                    showSnackbar(err.message || "Failed to perform action", "error");
                });

        }
    };

    const handleAddMemberTrigger = () => {
        handleHeaderMenuClose();
        setPage(1);
        setConnections([]);
        setHasMore(true);
        setView('add_member');
    };

    const handleBackToMain = () => {
        setView('main');
        setConnectionSearchQuery("");
    };

    const handleAddConnectionToGroup = (user) => {
        const data = { user_ids: [user.id || user.user_id] };
        addMember(chat.chat_id, data)
            .then(() => chatSettings(chat.chat_id))
            .then((updatedDetails) => setChatDetails(updatedDetails))
            .catch((err) => console.error("Error adding member:", err));
    };

    const handleExitGroupConfirm = () => {
        exitGroup(chat.chat_id)
            .then((res) => {
                console.log("Exited group:", res);
                setOpenLeaveDialog(false);
                onClose();
                setSelectedChat(null);
                setRecentChats((prevChats) =>
                    prevChats.filter(chatItem => chatItem.chat_id !== chat.chat_id)
                );
            })
            .catch((err) => console.error("Error exiting group:", err));
    };

    // Trigger next page load
    const loadMoreConnections = () => {
        setPage((prevPage) => prevPage + 1);
    };

    if (!chat) return null;

    // =========================================================
    // VIEW 1: MAIN DETAILS VIEW
    // =========================================================
    if (view === 'main') {
        return (
            <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                        <IconButton onClick={onClose} edge="start" sx={{ mr: 1 }}><Close /></IconButton>
                        <Typography variant="h6" noWrap>{isGroup ? "Group Info" : "Contact Info"}</Typography>
                    </Box>
                    {isGroup && (
                        <IconButton onClick={handleHeaderMenuOpen}><MoreVert /></IconButton>
                    )}
                </Box>

                <Menu anchorEl={headerAnchorEl} open={isHeaderMenuOpen} onClose={handleHeaderMenuClose}>
                    {amIAdmin && !loading && (
                        <MenuItem onClick={() => { setEditDialogOpen(true); handleHeaderMenuClose(); }}>
                            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                            <ListItemText>Edit Details</ListItemText>
                        </MenuItem>
                    )}
                    {amIAdmin && !loading && (
                        <>
                            <Divider />
                            <MenuItem onClick={handleAddMemberTrigger}>
                                <ListItemIcon><PersonAdd fontSize="small" /></ListItemIcon>
                                <ListItemText>Add Members</ListItemText>
                            </MenuItem>
                            <Divider />
                        </>
                    )}

                    {isGroup && !loading && (
                        <MenuItem onClick={() => setOpenLeaveDialog(true)}>
                            <ListItemIcon><ExitToApp fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText color='error'>
                                Leave & Delete Chat
                            </ListItemText>
                        </MenuItem>
                    )}


                </Menu>

                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {loading ? <Skeleton variant="circular" width={120} height={120} /> :
                            displayInfo.mentor ? (
                                <MentorBadge>
                                    <Avatar src={getImageUrlWithToken(displayInfo.image)} sx={{ width: 120, height: 120 }}>
                                        {isGroup ? <Groups sx={{ fontSize: 60 }} /> : displayInfo.name?.charAt(0)}
                                    </Avatar>
                                </MentorBadge>
                            ) : (
                                <Avatar src={getImageUrlWithToken(displayInfo.image)} sx={{ width: 120, height: 120 }}>
                                    {isGroup ? <Groups sx={{ fontSize: 60 }} /> : displayInfo.name?.charAt(0)}
                                </Avatar>
                            )
                        }
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                            <Typography variant="h6" align="center" fontWeight="600" sx={{ mt: 1 }}>
                                {loading ? <Skeleton width={150} /> : displayInfo.name}
                            </Typography>
                            {displayInfo.is_verified && <VerifiedUser fontSize="small" color="success" sx={{ fontSize: 16, mb: 0.3 }} />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2, mt: 0.5 }}>
                            {loading ? <Skeleton width={200} /> : displayInfo.desc}
                        </Typography>

                        {!isGroup && !loading && (
                            <Tooltip title="View Profile">
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2, textTransform: 'none', borderRadius: 4 }}
                                    onClick={() => window.location.href = "/profile/" + displayInfo.username}
                                    startIcon={<Person />}
                                    disabled={displayInfo.name === "Unknown" || displayInfo.name === "unknown"}
                                >
                                    View Profile
                                </Button>
                            </Tooltip>
                        )}
                    </Box>

                    <Divider />

                    {isGroup && (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2" fontWeight="bold" color="text.secondary">MEMBERS ({displayInfo.totalMembers})</Typography>
                                </Box>
                                <TextField
                                    size="small" fullWidth placeholder="Search members..."
                                    value={memberSearchQuery}
                                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>), sx: { bgcolor: 'background.paper' } }}
                                />
                            </Box>
                            <List disablePadding sx={{ flex: 1 }}>
                                {loading ? Array(3).fill(0).map((_, i) => <MemberSkeleton key={i} />) : filteredMembers.length > 0 ? (
                                    filteredMembers.map((member) => (
                                        <MemberListItem
                                            key={member.user_id}
                                            member={member}
                                            currentUserId={currentUserId}
                                            amIAdmin={amIAdmin}
                                            getImageUrlWithToken={getImageUrlWithToken}
                                            onAction={(action, id) => handleClick(action, id)}
                                            navigate={navigate}
                                            closeChat={closeChat}
                                        />
                                    ))
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No members found</Typography></Box>
                                )}
                            </List>
                        </Box>
                    )}
                </Box>


                <EditGroupDetails
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    chatDetails={chatDetails}
                    setChatDetails={setChatDetails}
                    setRecentChats={setRecentChats}
                    setSelectedChat={setSelectedChat}
                    getImageUrlWithToken={getImageUrlWithToken}
                />


                <Dialog open={openLeaveDialog} onClose={() => setOpenLeaveDialog(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Exit Group?</DialogTitle>
                    <DialogContent><DialogContentText>Are you sure you want to exit this group?</DialogContentText></DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenLeaveDialog(false)}>Cancel</Button>
                        <Button color="primary" variant="contained" onClick={handleExitGroupConfirm}>Exit</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // =========================================================
    // VIEW 2: ADD MEMBERS VIEW (React Infinite Scroll Component)
    // =========================================================
    if (view === 'add_member') {
        return (
            <Box sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', height: '100%', borderLeft: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <IconButton onClick={handleBackToMain} edge="start" sx={{ mr: 1 }}><ArrowBack /></IconButton>
                    <Typography variant="h6">Add Members</Typography>
                </Box>

                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover }}>
                    <TextField
                        autoFocus size="small" fullWidth placeholder="Search your connections..."
                        value={connectionSearchQuery}
                        onChange={(e) => setConnectionSearchQuery(e.target.value)}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>), sx: { bgcolor: 'background.paper' } }}
                    />
                </Box>

                {/* SCROLL CONTAINER 
                    We give this ID to the Box and pass it to InfiniteScroll so it listens 
                    to this specific container's scroll events, not the window.
                */}
                <Box
                    id="add-member-scrollable-div"
                    sx={{ flex: 1, overflowY: 'auto' }}
                >
                    <InfiniteScroll
                        dataLength={connections.length} // Used to calculate when to fetch more
                        next={loadMoreConnections} // Function to fetch next page
                        hasMore={hasMore} // Boolean to tell if there is more data
                        loader={
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, overflow: 'hidden' }}>
                                <CircularProgress size={24} />
                            </Box>
                        }
                        scrollableTarget="add-member-scrollable-div" // Must match the ID of the parent Box
                        endMessage={
                            !loadingConnections && connections.length > 0 && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        No more connections to show
                                    </Typography>
                                </Box>
                            )
                        }
                    >
                        {connections.length > 0 ? (
                            <List disablePadding>
                                {connections.map((user) => {
                                    const isMember = isAlreadyMember(user.id || user.user_id);
                                    return (
                                        <ListItem
                                            key={user.user_id || user.id}
                                            divider
                                            secondaryAction={
                                                isMember ? (
                                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                                        <CheckCircle color="success" fontSize="small" />
                                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">MEMBER</Typography>
                                                    </Stack>
                                                ) : (
                                                    <Button variant="outlined" size="small" startIcon={<AddCircleOutline />} onClick={() => handleAddConnectionToGroup(user)} sx={{ borderRadius: 4, textTransform: 'none' }}>
                                                        Add
                                                    </Button>
                                                )
                                            }
                                        >
                                            <ListItemAvatar>
                                                {user.is_mentor === 1 || Boolean(user.is_mentor) ? (
                                                    <MentorBadge isSmall>
                                                        <Avatar src={getImageUrlWithToken(user.profile_pic)}>
                                                            {user.name?.charAt(0)}
                                                        </Avatar>
                                                    </MentorBadge>
                                                ) : (
                                                    <Avatar src={getImageUrlWithToken(user.profile_pic)}>
                                                        {user.name?.charAt(0)}
                                                    </Avatar>
                                                )}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<Typography variant="body1" fontWeight="500">{user.name}</Typography>}
                                                secondary={user.describes_me}
                                                sx={{ opacity: isMember ? 0.6 : 1 }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        ) : (
                            !loadingConnections && (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No connections found</Typography>
                                </Box>
                            )
                        )}

                        {/* Initial Loading State (Page 1) */}
                        {loadingConnections && page === 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress size={30} />
                            </Box>
                        )}
                    </InfiniteScroll>
                </Box>
            </Box>
        );
    }
};

// --- Sub-Components (Same as before) ---
const MemberListItem = ({ member, currentUserId, amIAdmin, getImageUrlWithToken, onAction }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const open = Boolean(anchorEl);
    const isMe = String(member.user_id) === String(currentUserId);
    const isTargetAdmin = Boolean(member.admin) || member.admin === 1;
    const isMentor = Boolean(member.is_mentor) || member.is_mentor === 1;
    const canManage = amIAdmin;

    const handleMenuOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const handleMenuClose = () => setAnchorEl(null);
    const handleActionClick = (action) => { onAction(action, member.user_id); handleMenuClose(); };

    const handleGotoProfile = (username) => {
        window.location.href = "/profile/" + username;
    }

    return (
        <>
            <ListItem alignItems="center" divider sx={{ pr: 9, cursor: 'pointer' }}
                secondaryAction={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {isTargetAdmin && <Chip onClick={() => { }} label="Admin" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }} />}
                        {canManage && <IconButton edge="end" onClick={handleMenuOpen} size="small"><MoreVert fontSize="small" /></IconButton>}
                    </Stack>
                }
                onClick={() => handleGotoProfile(member.username)}
            >
                <ListItemAvatar>
                    {isMentor ? (
                        <MentorBadge isSmall>
                            <Avatar
                                src={getImageUrlWithToken(member.thumbnail_pic)}
                            >
                                {!member.thumbnail_pic && member.name?.charAt(0)}
                            </Avatar>
                        </MentorBadge>
                    ) : (
                        <Avatar src={getImageUrlWithToken(member.thumbnail_pic)}>
                            {!member.thumbnail_pic && member.name?.charAt(0)}
                        </Avatar>
                    )}

                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontWeight={isMe ? "600" : "400"}>{member.name} {" "}
                                {isMe && "(You)"}
                            </Typography>
                            {member.is_verified && <VerifiedUser fontSize="small" color="success" sx={{ fontSize: 16, mb: 0.3 }} />}
                        </Stack>
                    }
                    secondary={<Typography variant="caption" color="text.secondary" noWrap display="block">{member.describes_me || "-"}</Typography>}
                />
            </ListItem>
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {isTargetAdmin ? (
                    <MenuItem onClick={() => handleActionClick('revoke_admin')}><ListItemIcon><Security fontSize="small" /></ListItemIcon><ListItemText>Revoke Admin</ListItemText></MenuItem>
                ) : (
                    <MenuItem onClick={() => handleActionClick('make_admin')}><ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon><ListItemText>Make Admin</ListItemText></MenuItem>
                )}
                {!isMe && <><Divider /><MenuItem onClick={() => handleActionClick('remove_member')}><ListItemIcon><PersonRemove fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText sx={{ color: theme.palette.error.main }}>Remove User</ListItemText></MenuItem></>}
            </Menu>
        </>
    );
};

const MemberSkeleton = () => (
    <ListItem divider>
        <ListItemAvatar><Skeleton variant="circular" width={40} height={40} /></ListItemAvatar>
        <ListItemText primary={<Skeleton width="60%" />} secondary={<Skeleton width="40%" />} />
    </ListItem>
);

export default ChatDetail;